import { Link } from 'react-router-dom'

import { FxIcon, FxStatusLabel } from '@/components/fx'
import { cn } from '@/lib/utils'
import { BINARY_LABELS, type RecordCell, type RecordColumn, type RecordRow } from './recordRows'

/**
 * The records table.
 *
 * One scroll container holds header and body, so the sticky header row and
 * the sticky `#` column scroll in sync by construction — the Flutter table
 * ran two scroll controllers and mirrored their offsets, which is a source of
 * drift and of one-frame lag that CSS simply does not have. The price is an
 * inner vertical scrollbar (a sticky `top` needs a scroll container of its
 * own), which is why the container is capped rather than growing with the
 * template's document count.
 */

const COL_INDEX = 'w-14 min-w-14'
const COL_FIELD = 'min-w-[9rem] max-w-[16rem]'
const COL_REVIEW = 'min-w-[11rem]'

export interface RecordsTableProps {
  templateId: string
  columns: RecordColumn[]
  rows: RecordRow[]
  /** The offset of the first row, so the `#` column keeps counting across
   *  loaded pages instead of restarting. */
  startIndex?: number
  /** True while an arm other than the delivered values is on screen: rows
   *  that reading never touched are marked. */
  showFallbackMarker?: boolean
  caption: string
}

export function RecordsTable({
  templateId,
  columns,
  rows,
  startIndex = 0,
  showFallbackMarker = false,
  caption,
}: RecordsTableProps) {
  return (
    <div className="max-h-[70vh] overflow-auto rounded-sm">
      <table className="w-full border-collapse text-body">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th
              scope="col"
              className={cn(
                'sticky left-0 top-0 z-20 bg-fill px-2 py-2 text-left text-small font-semibold',
                COL_INDEX,
              )}
            >
              #
            </th>
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={cn(
                  'sticky top-0 z-10 whitespace-nowrap bg-fill px-2 py-2 text-left text-small font-semibold',
                  COL_FIELD,
                )}
              >
                {column.name}
              </th>
            ))}
            <th
              scope="col"
              className={cn(
                'sticky top-0 z-10 bg-fill px-2 py-2 text-left text-small font-semibold',
                COL_REVIEW,
              )}
            >
              Prüfung
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.documentId} className="border-b border-divider">
              <th
                scope="row"
                className={cn(
                  'sticky left-0 z-10 bg-surface px-2 py-2 text-left font-normal text-text-muted',
                  COL_INDEX,
                )}
                title={row.title}
              >
                {startIndex + index + 1}
                {showFallbackMarker && row.fallbackToDelivered && (
                  <>
                    <span aria-hidden="true" className="text-text-muted">
                      {' *'}
                    </span>
                    <span className="sr-only">
                      Diese Lesung wurde für dieses Dokument nicht ausgeführt; angezeigt sind die
                      gelieferten Werte.
                    </span>
                  </>
                )}
              </th>
              {columns.map((column) => (
                <Cell key={column.id} cell={row.cells[column.id]} />
              ))}
              <td className={cn('px-2 py-2', COL_REVIEW)}>
                <ReviewLink templateId={templateId} row={row} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Cell({ cell }: { cell?: RecordCell }) {
  // A field the document's layout does not carry is not an empty value: the
  // 2019 form never asked. It stays blank and is never marked for review.
  if (!cell || !cell.present) {
    return (
      <td className={cn('px-2 py-2 text-text-muted', COL_FIELD)}>
        <span aria-hidden="true">–</span>
        <span className="sr-only">In diesem Layout nicht enthalten</span>
      </td>
    )
  }

  if (cell.binary) {
    return (
      <td className={cn('px-2 py-2', COL_FIELD, cell.flagged && 'bg-danger-subtle')}>
        {cell.binary === 'checked' ? (
          <FxIcon name="check_circle" size={20} className="text-success-text" />
        ) : (
          <span aria-hidden="true" className="text-text-muted">
            –
          </span>
        )}
        <span className="sr-only">{BINARY_LABELS[cell.binary]}</span>
      </td>
    )
  }

  const empty = cell.value.trim() === ''
  return (
    <td
      className={cn(
        'px-2 py-2',
        COL_FIELD,
        cell.flagged && 'bg-danger-subtle',
        empty && 'text-text-muted',
      )}
      // The whole value, for a cell that had to be truncated to fit.
      title={empty ? undefined : cell.value}
    >
      <span className="flex items-center gap-1">
        <span className="truncate">{empty ? '–' : cell.value}</span>
        {cell.reviewed && (
          <>
            <FxIcon name="check_circle" size={14} className="shrink-0 text-success-text" />
            <span className="sr-only">geprüft</span>
          </>
        )}
      </span>
    </td>
  )
}

/**
 * The "Prüfung" cell: a link into the review screen for this document.
 *
 * The words are the design system's four, not the artboard's "prüfen" /
 * "geprüft" — "geprüft" on a document nobody has looked at would be a claim
 * the data does not support. A document with flagged fields is `unklar`, one
 * a person has confirmed is `geprüft`, and one the analyzer was happy with
 * and nobody checked is `analysiert`.
 */
function ReviewLink({ templateId, row }: { templateId: string; row: RecordRow }) {
  const flagged = row.needsReviewCount > 0
  return (
    <Link
      to={`/templates/${templateId}/dokumente/${row.documentId}`}
      className="inline-flex rounded-sm no-underline"
    >
      <FxStatusLabel
        status={flagged ? 'unclear' : 'done'}
        reviewed={!flagged && row.reviewed}
        size="small"
      />
      <span className="sr-only">
        {` – ${row.title || 'Datensatz'} öffnen`}
        {flagged
          ? `, ${row.needsReviewCount} ${row.needsReviewCount === 1 ? 'Feld' : 'Felder'} zu prüfen`
          : ''}
      </span>
    </Link>
  )
}
